import {
  DEFAULT_MAIL_COLUMN_ORDER,
  DEFAULT_MAIL_COLUMN_WIDTHS,
  defaultDisplayPreferences,
  type DisplayPreferences,
  type CalendarSchedulingPreferences,
  type MailColumn,
  type MailColumnWidths,
  type SavedMailSearch,
} from '@email/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePref = DisplayPreferences['theme'];
export type AccountIdentityMode = DisplayPreferences['accountIdentityMode'];
export type FolderPlacement = DisplayPreferences['folderPlacementById'][string];
export type MailReadingLayout = DisplayPreferences['mailReadingLayout'];
export type MailListDensity = DisplayPreferences['mailListDensity'];
export type MailSenderSubjectLayout = DisplayPreferences['mailSenderSubjectLayout'];
export type RemoteImagePolicy = DisplayPreferences['remoteImagePolicy'];
export type CalendarView = DisplayPreferences['calendarView'];

interface UiState {
  theme: ThemePref;
  navCollapsed: boolean;
  defaultComposeAccountId: number | null;
  defaultCalendarId: number | null;
  calendarView: CalendarView;
  calendarShowTasks: boolean;
  calendarScheduling: CalendarSchedulingPreferences;
  plannerNativeNotifications: boolean;
  undoSendSeconds: number;
  savedMailSearches: SavedMailSearch[];
  accountOrder: number[];
  calendarOrder: number[];
  collapsedAccountIds: number[];
  expandedMoreAccountIds: number[];
  folderOrderByAccount: Record<string, number[]>;
  folderPlacementById: Record<string, FolderPlacement>;
  accountIdentityMode: AccountIdentityMode;
  mailReadingLayout: MailReadingLayout;
  mailListDensity: MailListDensity;
  mailSenderSubjectLayout: MailSenderSubjectLayout;
  remoteImagePolicy: RemoteImagePolicy;
  mailColumnOrder: MailColumn[];
  hiddenMailColumns: DisplayPreferences['hiddenMailColumns'];
  mailColumnWidths: MailColumnWidths;
  verticalSplitPercent: number;
  horizontalSplitPercent: number;
  setTheme: (theme: ThemePref) => void;
  setDefaultComposeAccountId: (accountId: number | null) => void;
  setDefaultCalendarId: (calendarId: number | null) => void;
  setCalendarView: (calendarView: CalendarView) => void;
  setCalendarShowTasks: (calendarShowTasks: boolean) => void;
  setCalendarScheduling: (
    calendarScheduling: CalendarSchedulingPreferences,
  ) => void;
  setPlannerNativeNotifications: (
    plannerNativeNotifications: boolean,
  ) => void;
  setUndoSendSeconds: (seconds: number) => void;
  setSavedMailSearches: (searches: SavedMailSearch[]) => void;
  setAccountOrder: (accountIds: number[]) => void;
  setCalendarOrder: (calendarIds: number[]) => void;
  toggleNav: () => void;
  toggleAccountCollapsed: (accountId: number) => void;
  toggleAccountMore: (accountId: number) => void;
  setFolderOrder: (accountId: number, folderIds: number[]) => void;
  setFolderPlacement: (folderId: number, placement: FolderPlacement) => void;
  resetFolderLayout: (accountId: number, folderIds: number[]) => void;
  setAccountIdentityMode: (mode: AccountIdentityMode) => void;
  setMailReadingLayout: (layout: MailReadingLayout) => void;
  setMailListDensity: (density: MailListDensity) => void;
  setMailSenderSubjectLayout: (layout: MailSenderSubjectLayout) => void;
  setRemoteImagePolicy: (policy: RemoteImagePolicy) => void;
  setMailColumnOrder: (columns: MailColumn[]) => void;
  setMailColumnVisible: (column: MailColumn, visible: boolean) => void;
  setMailColumnWidth: (column: MailColumn, width: number) => void;
  setVerticalSplitPercent: (percent: number) => void;
  setHorizontalSplitPercent: (percent: number) => void;
  resetMailView: () => void;
  applyDisplayPreferences: (preferences: DisplayPreferences) => void;
}

const defaults = defaultDisplayPreferences();

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      ...defaults,
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      setDefaultComposeAccountId: (defaultComposeAccountId) =>
        set({ defaultComposeAccountId }),
      setDefaultCalendarId: (defaultCalendarId) => set({ defaultCalendarId }),
      setCalendarView: (calendarView) => set({ calendarView }),
      setCalendarShowTasks: (calendarShowTasks) => set({ calendarShowTasks }),
      setCalendarScheduling: (calendarScheduling) =>
        set({
          calendarScheduling: {
            ...calendarScheduling,
            weekdays: [...calendarScheduling.weekdays],
            calendarIds: [...calendarScheduling.calendarIds],
          },
        }),
      setPlannerNativeNotifications: (plannerNativeNotifications) =>
        set({ plannerNativeNotifications }),
      setUndoSendSeconds: (undoSendSeconds) => set({ undoSendSeconds }),
      setSavedMailSearches: (savedMailSearches) =>
        set({ savedMailSearches: normalizeSavedMailSearches(savedMailSearches) }),
      setAccountOrder: (accountOrder) => set({ accountOrder: [...accountOrder] }),
      setCalendarOrder: (calendarOrder) => set({ calendarOrder: [...calendarOrder] }),
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
      toggleAccountCollapsed: (accountId) =>
        set((state) => ({
          collapsedAccountIds: toggleId(state.collapsedAccountIds, accountId),
        })),
      toggleAccountMore: (accountId) =>
        set((state) => ({
          expandedMoreAccountIds: toggleId(state.expandedMoreAccountIds, accountId),
        })),
      setFolderOrder: (accountId, folderIds) =>
        set((state) => ({
          folderOrderByAccount: {
            ...state.folderOrderByAccount,
            [String(accountId)]: folderIds,
          },
        })),
      setFolderPlacement: (folderId, placement) =>
        set((state) => ({
          folderPlacementById: {
            ...state.folderPlacementById,
            [String(folderId)]: placement,
          },
        })),
      resetFolderLayout: (accountId, folderIds) =>
        set((state) => {
          const folderOrderByAccount = { ...state.folderOrderByAccount };
          delete folderOrderByAccount[String(accountId)];
          const folderPlacementById = { ...state.folderPlacementById };
          for (const folderId of folderIds) delete folderPlacementById[String(folderId)];
          return { folderOrderByAccount, folderPlacementById };
        }),
      setAccountIdentityMode: (accountIdentityMode) => set({ accountIdentityMode }),
      setMailReadingLayout: (mailReadingLayout) => set({ mailReadingLayout }),
      setMailListDensity: (mailListDensity) => set({ mailListDensity }),
      setMailSenderSubjectLayout: (mailSenderSubjectLayout) =>
        set({ mailSenderSubjectLayout }),
      setRemoteImagePolicy: (remoteImagePolicy) => set({ remoteImagePolicy }),
      setMailColumnOrder: (mailColumnOrder) => set({ mailColumnOrder: [...mailColumnOrder] }),
      setMailColumnVisible: (column, visible) =>
        set((state) => {
          if (visible) {
            return {
              hiddenMailColumns: state.hiddenMailColumns.filter(
                (candidate) => candidate !== column,
              ),
            };
          }
          if (
            column === 'subject' ||
            state.hiddenMailColumns.some((candidate) => candidate === column)
          ) {
            return { hiddenMailColumns: state.hiddenMailColumns };
          }
          return { hiddenMailColumns: [...state.hiddenMailColumns, column] };
        }),
      setMailColumnWidth: (column, width) =>
        set((state) => ({
          mailColumnWidths: { ...state.mailColumnWidths, [column]: width },
        })),
      setVerticalSplitPercent: (verticalSplitPercent) => set({ verticalSplitPercent }),
      setHorizontalSplitPercent: (horizontalSplitPercent) => set({ horizontalSplitPercent }),
      resetMailView: () =>
        set({
          accountIdentityMode: defaults.accountIdentityMode,
          mailReadingLayout: defaults.mailReadingLayout,
          mailListDensity: defaults.mailListDensity,
          mailSenderSubjectLayout: defaults.mailSenderSubjectLayout,
          remoteImagePolicy: defaults.remoteImagePolicy,
          mailColumnOrder: [...DEFAULT_MAIL_COLUMN_ORDER],
          hiddenMailColumns: [],
          mailColumnWidths: { ...DEFAULT_MAIL_COLUMN_WIDTHS },
          verticalSplitPercent: defaults.verticalSplitPercent,
          horizontalSplitPercent: defaults.horizontalSplitPercent,
        }),
      applyDisplayPreferences: (preferences) => {
        set({
          ...preferences,
          calendarScheduling: {
            ...preferences.calendarScheduling,
            weekdays: [...preferences.calendarScheduling.weekdays],
            calendarIds: [...preferences.calendarScheduling.calendarIds],
          },
          savedMailSearches: preferences.savedMailSearches.map((search) => ({ ...search })),
          accountOrder: [...preferences.accountOrder],
          calendarOrder: [...preferences.calendarOrder],
          collapsedAccountIds: [...preferences.collapsedAccountIds],
          expandedMoreAccountIds: [...preferences.expandedMoreAccountIds],
          folderOrderByAccount: { ...preferences.folderOrderByAccount },
          folderPlacementById: { ...preferences.folderPlacementById },
          mailColumnOrder: [...preferences.mailColumnOrder],
          hiddenMailColumns: [...preferences.hiddenMailColumns],
          mailColumnWidths: { ...preferences.mailColumnWidths },
        });
        applyTheme(preferences.theme);
      },
    }),
    {
      name: 'ui',
      version: 1,
      migrate: (persisted) => {
        const state = persisted as Partial<UiState>;
        return {
          ...state,
          savedMailSearches: normalizeSavedMailSearches(state.savedMailSearches ?? []),
        } as UiState;
      },
    },
  ),
);

export function currentDisplayPreferences(): DisplayPreferences {
  const state = useUiStore.getState();
  return {
    theme: state.theme,
    navCollapsed: state.navCollapsed,
    defaultComposeAccountId: state.defaultComposeAccountId,
    defaultCalendarId: state.defaultCalendarId,
    calendarView: state.calendarView,
    calendarShowTasks: state.calendarShowTasks,
    calendarScheduling: {
      ...state.calendarScheduling,
      weekdays: [...state.calendarScheduling.weekdays],
      calendarIds: [...state.calendarScheduling.calendarIds],
    },
    plannerNativeNotifications: state.plannerNativeNotifications,
    undoSendSeconds: state.undoSendSeconds,
    savedMailSearches: normalizeSavedMailSearches(state.savedMailSearches),
    accountOrder: [...state.accountOrder],
    calendarOrder: [...state.calendarOrder],
    collapsedAccountIds: [...state.collapsedAccountIds],
    expandedMoreAccountIds: [...state.expandedMoreAccountIds],
    folderOrderByAccount: { ...state.folderOrderByAccount },
    folderPlacementById: { ...state.folderPlacementById },
    accountIdentityMode: state.accountIdentityMode,
    mailReadingLayout: state.mailReadingLayout,
    mailListDensity: state.mailListDensity,
    mailSenderSubjectLayout: state.mailSenderSubjectLayout,
    remoteImagePolicy: state.remoteImagePolicy,
    mailColumnOrder: [...state.mailColumnOrder],
    hiddenMailColumns: [...state.hiddenMailColumns],
    mailColumnWidths: { ...state.mailColumnWidths },
    verticalSplitPercent: state.verticalSplitPercent,
    horizontalSplitPercent: state.horizontalSplitPercent,
  };
}

function normalizeSavedMailSearches(
  searches: readonly (Omit<SavedMailSearch, 'sort'> & {
    sort?: SavedMailSearch['sort'];
  })[],
): SavedMailSearch[] {
  return searches.map((search) => ({
    ...search,
    sort: search.sort === 'relevance' ? 'relevance' : 'newest',
  }));
}

function toggleId(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((candidate) => candidate !== id) : [...ids, id];
}

export function applyTheme(pref: ThemePref): void {
  const resolved =
    pref === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : pref;
  document.documentElement.dataset.theme = resolved;
}

export function initTheme(): void {
  applyTheme(useUiStore.getState().theme);
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (useUiStore.getState().theme === 'system') applyTheme('system');
  });
}
